import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { EDocumentPackageSignerRole } from "@rentiohq/shared-frontend/dist/reduxV2/documentPackage/documentPackage.types";
import {
  EDocumentPackageSignerStatus,
  EDocumentPackageStatus,
  EDocumentPackageTypes,
  IDocumentPackage,
  IDocumentPackageSigner,
} from "@rentiohq/shared-frontend/dist/types/documentPackage.types";
import { Icon, TextStyle } from "@rentiohq/web-shared/dist/components";
import {
  EDocumentPackageStageSimple,
  TAppearance,
} from "@rentiohq/web-shared/dist/types";
import utils from "@rentiohq/web-shared/dist/utils";
import { compact } from "lodash";
import { ts } from "../services";
import { renderTooltipChecklist } from "./tooltip.utils";

export const getUniqueSigners = (
  documentPackage: IDocumentPackage,
): IDocumentPackageSigner[] => {
  if (!documentPackage?.signers) {
    return [];
  }

  if (documentPackage.type === EDocumentPackageTypes.RentDeposit) {
    return documentPackage.signers.filter(
      signer => signer.role !== EDocumentPackageSignerRole.Payer,
    );
  }

  return documentPackage.signers;
};

export const findUniqueSigner = (
  documentPackage: IDocumentPackage,
  accountIds: number[],
) =>
  getUniqueSigners(documentPackage).find(signer =>
    accountIds.includes(signer.accountId),
  );

export const renderSignersTooltip = (documentPackage: IDocumentPackage) => {
  const checklist = getUniqueSigners(documentPackage).map(
    (signer: IDocumentPackageSigner) => {
      const hasSigned =
        signer.hasOwnProperty("status") &&
        signer.status === EDocumentPackageSignerStatus.Finished;

      return {
        label: (
          <p style={{ display: "flex", alignItems: "center" }}>
            {hasSigned ? (
              <Icon source="checked" size="medium" />
            ) : (
              <Icon source="cross" size="medium" />
            )}{" "}
            {getName(signer, true)}
          </p>
        ),
        success: hasSigned,
      };
    },
  );

  return renderTooltipChecklist(checklist);
};

export const getSignerInfo = (documentPackage: IDocumentPackage) => {
  const uniqueSigners = getUniqueSigners(documentPackage);

  const signedCount = uniqueSigners.filter(
    signer =>
      signer.hasOwnProperty("status") &&
      signer.status === EDocumentPackageSignerStatus.Finished,
  ).length;
  const signerCount = uniqueSigners.length;

  return {
    signedCount,
    signerCount,
  };
};

export const getAdvancedStages = (stage: EDocumentPackageStageSimple) => {
  switch (stage) {
    case EDocumentPackageStageSimple.Finished:
      return [EDocumentPackageStatus.Finished];

    default:
      return [
        EDocumentPackageStatus.New,
        EDocumentPackageStatus.Draft,
        EDocumentPackageStatus.Pending,
        EDocumentPackageStatus.PDFReady,
      ];
  }
};

export const getStatusStageVariation = (
  status?: EDocumentPackageStatus,
): TAppearance => {
  switch (status) {
    case EDocumentPackageStatus.Finished:
      return "success";

    case EDocumentPackageStatus.Pending:
      return "warning";

    case EDocumentPackageStatus.WorkerFailed:
    case EDocumentPackageStatus.Rejected:
    case EDocumentPackageStatus.Expired:
    case EDocumentPackageStatus.Failed:
      return "error";

    case EDocumentPackageStatus.New:
    case EDocumentPackageStatus.PDFReady:
    case EDocumentPackageStatus.Draft:
    case EDocumentPackageStatus.Revoked:
    default:
      return "default";
  }
};

export const getSimpleStages = (status: EDocumentPackageStatus) => {
  switch (status) {
    case EDocumentPackageStatus.New:
    case EDocumentPackageStatus.PDFReady:
    case EDocumentPackageStatus.Pending:
    case EDocumentPackageStatus.Draft:
      return EDocumentPackageStageSimple.Pending;

    case EDocumentPackageStatus.Finished:
      return EDocumentPackageStageSimple.Finished;

    case EDocumentPackageStatus.Revoked:
      return EDocumentPackageStageSimple.Revoked;

    default:
      return EDocumentPackageStageSimple.Failed;
  }
};

export const renderSignerSubtitle = (signer: IDocumentPackageSigner) => {
  const hasSigned = signer.status === EDocumentPackageSignerStatus.Finished;
  return (
    <>
      <TextStyle variation={"subdued"} element="div">
        {[
          signer.role && ts.role(signer.role),
          !signer.usedSigningType &&
            !!signer.allowedSigningTypes &&
            ts.documentPackageSignerToSignWith({
              values: {
                methods: signer.allowedSigningTypes
                  .map(type => ts.documentPackageSigningType(type))
                  .join(", "),
              },
            }),
        ]
          .filter(Boolean)
          .join(" | ")}
        {}
      </TextStyle>
      <TextStyle variation={hasSigned ? "positive" : "warn"} element="div">
        {!!signer.usedSigningType && signer.completedAt ? (
          ts.documentPackageSignerUsedSigningType({
            values: {
              method: ts.documentPackageSigningType(signer.usedSigningType),
              date: signer.completedAt
                ? utils.date.format(signer.completedAt, "dd/MM/yyyy HH:mm")
                : undefined,
            },
          })
        ) : (
          <>
            {ts.system(hasSigned ? "signed_at" : "not_signed")}{" "}
            {hasSigned && signer.completedAt
              ? utils.date.format(signer.completedAt, "dd/MM/yyyy HH:mm")
              : undefined}
          </>
        )}
      </TextStyle>
    </>
  );
};

export const getDocumentPackageActivityFilter = (params: {
  documentPackage: IDocumentPackage;
}) => {
  const { documentPackage } = params;

  const { id: documentPackageId, documentIdInPackage } = documentPackage;

  return {
    where: {
      or: compact([
        { documentPackageId },
        documentIdInPackage ? { documentId: documentIdInPackage } : undefined,
      ]),
    },
  };
};

export const checkIfRevokable = (documentPackage: IDocumentPackage) => {
  if (!documentPackage.status) {
    return false;
  }
  const simpleStage = getSimpleStages(documentPackage.status);
  if (
    [
      EDocumentPackageTypes.RentDeposit,
      EDocumentPackageTypes.RentDepositRelease,
    ].includes(documentPackage.type)
  ) {
    return false;
  }
  if (simpleStage !== EDocumentPackageStageSimple.Pending) {
    return false;
  }

  return true;
};

export const hasAllTenantSignatures = (documentPackage: IDocumentPackage) => {
  const unsignedTenants = documentPackage.signers.filter(
    signer =>
      signer.role === "TENANT" &&
      signer.status !== EDocumentPackageSignerStatus.Finished,
  );
  return unsignedTenants.length !== 0;
};
